import {sendStats} from '@github-ui/stats'
import {getSoftNavMechanism, getSoftNavReactAppName, getSoftNavReferrer} from './utils'
import type {SoftNavFailReason, SoftNavMechanism} from './events'
import {getCurrentReactAppName} from '@github-ui/stats-metadata'

export const SOFT_NAV_DURATION_MARK = 'stats:soft-nav-duration'
export const MECHANISM_MAPPING: Record<SoftNavMechanism | 'hard', PlatformBrowserSoftNavigationMechanism> = {
  react: 'REACT',
  'turbo.frame': 'FRAME',
  'turbo.error': 'TURBO.ERROR',
  ui: 'UI',
  hard: 'HARD',
  unknown: 'UNKNOWN',
}
export function markStart() {
  // browswers only record the first ~150 resources
  // clearing it here provides room to track additional resources loaded during the soft-nav
  window.performance.clearResourceTimings()
  window.performance.mark(SOFT_NAV_DURATION_MARK)
}

function getDurationSinceLastSoftNav() {
  if (performance.getEntriesByName(SOFT_NAV_DURATION_MARK).length === 0) {
    return null
  }

  performance.measure(SOFT_NAV_DURATION_MARK, {
    start: SOFT_NAV_DURATION_MARK,
    detail: {
      devtools: {
        dataType: 'track-entry',
        track: 'Navigation',
        trackGroup: 'Performance Timeline',
        color: 'secondary',
        tooltipText: 'Soft nav duration',
      },
    },
  })
  const measures = performance.getEntriesByName(SOFT_NAV_DURATION_MARK)
  const measure = measures.pop()
  return measure ? measure.duration : null
}

export function sendFailureStats(turboFailureReason: string, mechanism = getSoftNavMechanism()) {
  const failReason = classifyFailReason(mechanism)
  const startUrl = getSoftNavReferrer()
  const endUrl = window.location.href
  const duration = getDurationSinceLastSoftNav()
  const softNavigationFailure: PlatformBrowserPerformanceSoftNavigationFailure = {
    mechanism: MECHANISM_MAPPING[mechanism],
    initiatorApp: getSoftNavReactAppName() || 'rails',
    destinationApp: getCurrentReactAppName() || 'rails',
    failReason,
    rawReason: turboFailureReason,
    duration: duration != null ? Math.round(duration) : undefined,
    startUrl,
    endUrl,
  }

  if (failReason === 'turbo_reload') {
    softNavigationFailure.recoveryAction = 'page_reloaded'
  }

  sendStats({
    turboFailureReason,
    turboStartUrl: startUrl,
    turboEndUrl: endUrl,
    softNavigationFailure,
  })
}

interface BailoutStats {
  startUrl: string
  endUrl: string
  initiatorApp: string
  destinationApp: string
}

/** Reports a hard-navigation bailout that skips the soft-navigation lifecycle. */
export function sendBailoutStats({startUrl, endUrl, initiatorApp, destinationApp}: BailoutStats) {
  sendStats({
    turboFailureReason: 'hard_navigate_bailout',
    turboStartUrl: startUrl,
    turboEndUrl: endUrl,
    softNavigationFailure: {
      mechanism: MECHANISM_MAPPING.hard,
      initiatorApp,
      destinationApp,
      failReason: 'hard_navigate_bailout',
      startUrl,
      endUrl,
    },
  })
}

function classifyFailReason(mechanism: SoftNavMechanism): SoftNavFailReason {
  if (mechanism === 'react') return 'route_error'
  if (mechanism === 'turbo.frame' || mechanism === 'turbo.error') return 'turbo_reload'
  return 'unknown'
}

export function sendRenderStats() {
  const duration = getDurationSinceLastSoftNav()

  if (!duration) return

  const mechanism = MECHANISM_MAPPING[getSoftNavMechanism()]
  const roundedDuration = Math.round(duration)

  if (mechanism === MECHANISM_MAPPING.react)
    document.dispatchEvent(new CustomEvent('staffbar-update', {detail: {duration: roundedDuration}}))

  sendStats({
    requestUrl: window.location.href,
    referredRequestUrl: getSoftNavReferrer(),
    softNavigationTiming: {
      mechanism,
      destination: getCurrentReactAppName() || 'rails',
      duration: roundedDuration,
      initiator: getSoftNavReactAppName() || 'rails',
    },
  })
}
